/**
 * Consent Client using tRPC
 *
 * This client connects to the backend consent API via tRPC to manage
 * GDPR consent for legal documents (terms, privacy policy).
 */

import { hashKey } from '@tanstack/react-query'
import { scopedQueryKey, useAuth } from '@xhubio-saas/core-frontend/kernel'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

/**
 * Missing consent information from backend
 */
export interface MissingConsent {
  type: string
  documentId: string
  version: number
  title: string
}

/**
 * Consent check result
 */
export interface ConsentCheckResult {
  allAccepted: boolean
  missing: MissingConsent[]
}

/**
 * Legal document from database
 */
export interface LegalDocument {
  id: string
  type: string
  version: string
  language: string
  title: string
  content: string
  effectiveAt: string
}

/**
 * Consent API client interface
 */
export interface ConsentApiClient {
  /** Check if user has accepted all required consents */
  checkRequired(): Promise<ConsentCheckResult>

  /**
   * Submit consent for a single document type.
   *
   * The current UI language travels along automatically (see
   * `useConsentClient`) — callers neither pass it nor can they forget it.
   */
  submit(documentType: string): Promise<{ success: boolean }>

  /**
   * Submit consent for multiple document types at once.
   *
   * The current UI language travels along automatically (see
   * `useConsentClient`) — callers neither pass it nor can they forget it.
   */
  submitMultiple(documentTypes: string[]): Promise<{ success: boolean }>

  /** Get the latest documents for all required consent types */
  getRequiredDocuments(): Promise<LegalDocument[]>
}

/**
 * Creates a Consent client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns ConsentApiClient implementation using tRPC
 */
export function useConsentClient(): ConsentApiClient {
  const utils = trpc.useUtils()
  const { i18n } = useTranslation()

  // 🔴 Die Sprache MUSS mitreisen. Ein Rechtsdokument liegt je Version als eine
  // Kopie PRO SPRACHE in der Datenbank (13 Stueck), und der Server bindet die
  // Einwilligung an genau die Kopie, die hier benannt wird — `consentService`
  // vergleicht die Sprachspalte exakt. Ohne Angabe nahm er stattdessen die
  // neueste Kopie irgendeiner Sprache: in der Produktion war das die
  // bulgarische, obwohl sie nie jemand zu Gesicht bekam. Die Einwilligung zeigte
  // damit auf einen Text, den der Nutzer nicht gelesen hat.
  //
  // Zwei-Buchstaben-Basis wie in `subscriptionClient.ts` ("de" aus "de-DE"):
  // die Sprachspalte fuehrt genau diese Codes. Ein voller Tag traefe wegen des
  // exakten Vergleichs keine Zeile und fiele still auf Englisch zurueck.
  const language = (i18n.language || 'en').split('-')[0] ?? 'en'

  // Get mutation functions
  const submitMutation = trpc.consent.submit.useMutation()
  const submitMultipleMutation = trpc.consent.submitMultiple.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  //
  // Die Sprache liegt bewusst in DERSELBEN Ref statt in der
  // Abhaengigkeitsliste des Memos: so behaelt der Client seine Identitaet ueber
  // einen Sprachwechsel hinweg (Consumer mit `useEffect([consentClient])`
  // laufen nicht neu an), und der Callback liest trotzdem die Sprache, die im
  // Moment des Klicks galt — also die, die der Nutzer gerade vor sich hatte.
  const mutations = useRef({
    submitMutation,
    submitMultipleMutation,
    language,
  })
  mutations.current = {
    submitMutation,
    submitMultipleMutation,
    language,
  }

  return useMemo(
    () => ({
      async checkRequired(): Promise<ConsentCheckResult> {
        return utils.consent.checkRequired.fetch()
      },

      async submit(documentType: string): Promise<{ success: boolean }> {
        const { submitMutation: mutation, language: uiLanguage } = mutations.current
        return mutation.mutateAsync({ documentType, language: uiLanguage })
      },

      async submitMultiple(documentTypes: string[]): Promise<{ success: boolean }> {
        const { submitMultipleMutation: mutation, language: uiLanguage } = mutations.current
        return mutation.mutateAsync({ documentTypes, language: uiLanguage })
      },

      async getRequiredDocuments(): Promise<LegalDocument[]> {
        return (await utils.consent.getRequiredDocuments.fetch()) as unknown as LegalDocument[]
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}

/**
 * Hook to use consent check query with React Query caching
 *
 * Uses 5 minute stale time to avoid refetching on every route change.
 *
 * 🔴 Die Antwort gilt je Konto: der Server liest `ctx.session.user.id`. Deshalb trägt der Cache-Eintrag die Nutzerkennung
 * (`queryKeyHashFn`): Nach einem Kontowechsel liest das neue Konto seinen eigenen Eintrag, nie den des vorigen. Ohne die
 * Kennung übernahm ein Kontowechsel im selben Tab bis zu 5 min das `allAccepted: true` des vorigen Kontos, und
 * `ConsentGuard` ließ ein Konto ohne Zustimmung in die App (Issue API 2026-11-03
 * `ia-registrierung-zustimmung-agb-datenschutz-wird-nicht-protokolliert`, Kandidat (b), ENTSCHEID observer 15989; Tabelle
 * `tests/consent-kontowechsel-im-selben-tab.tabelle.md`). Die Kennung geht nur in den Schlüssel, nicht an den Server — er kennt
 * die Sitzung selbst.
 *
 * ⚪ Bewusst nur der Schlüssel, keine Entwertung hier (ENTSCHEID observer 16023, Hausregel „Wer schreibt, entwertet“: ein Leser
 * entwertet nie). Muss beim Anmelden, Abmelden oder Kontowechsel aufgeräumt werden, gehört das in diese Mutationen.
 */
export function useConsentCheckQuery(language?: string) {
  const { session } = useAuth()
  const nutzer = session?.user?.id ?? null

  return trpc.consent.checkRequired.useQuery(language ? { language } : undefined, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection)
    refetchOnWindowFocus: false,
    retry: 1,
    // Hausform `scopedQueryKey` (core-frontend, Präzedenz `preferences.get`): die Kennung hinten an den Schlüssel. tRPC lässt
    // `queryKey` nicht setzen, deshalb über den Hash; `utils.consent.checkRequired.invalidate()` trifft weiter alle Einträge.
    queryKeyHashFn: (key) => hashKey(scopedQueryKey(key, nutzer)),
  })
}

/**
 * Hook to get required documents with caching
 */
export function useRequiredDocumentsQuery(language?: string) {
  return trpc.consent.getRequiredDocuments.useQuery(language ? { language } : undefined, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  })
}
